/**
 * Onglet Admin > OCR Queue — visibilité + actions sur les pièces OCR.
 *
 * Sprint big-PDF V2.7 + V2.4 (2026-05-22). Consomme GET /admin/ocr/queue.
 * - Counts par bucket (PdfFile / PieceClient / PieceAdverse) × status
 * - Liste pending/in_progress/failed × 30 lignes par bucket
 * - Bouton "Re-OCR Mistral" sur chaque ligne (POST /admin/ocr/reocr)
 * - Bouton "Backfill basse confiance" en pied (POST /admin/ocr/backfill-mistral)
 */
import { useEffect, useState, useCallback } from 'react';
import { ApiError, api } from '../../lib/api';
import Btn from '../ui/Btn';
import { toast } from '../../stores/toaster';

type StatusCount = { source_type: string; ocr_status: string; n: number };
type QueueRow = {
  id: string;
  ocr_status: string;
  ocr_engine: string | null;
  uploaded_at: string;
  filename_original: string;
  dossier_ref: string | null;
};
type QueuePayload = {
  counts: StatusCount[];
  pdf_files_detail: QueueRow[];
  pieces_client_detail: QueueRow[];
  pieces_adverses_detail: QueueRow[];
};

const BUCKETS = [
  { key: 'pdf_file', label: 'PdfFile', detail: 'pdf_files_detail' as const },
  { key: 'piece_client', label: 'PieceClient', detail: 'pieces_client_detail' as const },
  { key: 'piece_adverse', label: 'PieceAdverse', detail: 'pieces_adverses_detail' as const },
];

const STATUS_COLOR: Record<string, string> = {
  done: 'text-cat-fav',
  pending: 'text-text-secondary',
  in_progress: 'text-cat-imp-text',
  failed: 'text-cat-def',
  processed: 'text-cat-fav',
  skipped: 'text-text-tertiary',
};

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-BE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function OcrQueueTab() {
  const [data, setData] = useState<QueuePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [backfillBusy, setBackfillBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<QueuePayload>('/admin/ocr/queue');
      setData(d);
    } catch (err) {
      const detail = err instanceof ApiError ? err.problem.detail || err.problem.title : String(err);
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const reocrMistral = useCallback(
    async (sourceType: string, sourceId: string) => {
      setActingId(sourceId);
      try {
        await api.post('/admin/ocr/reocr', {
          source_type: sourceType,
          source_id: sourceId,
          force_mistral: true,
        });
        toast.success('Re-OCR Mistral enqueued');
        void load();
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Erreur re-OCR');
      } finally {
        setActingId(null);
      }
    },
    [load],
  );

  const backfillLowConf = useCallback(async () => {
    if (!confirm('Backfill Mistral des PdfFile Tesseract basse confiance ? (max 50, coût estimé ~0.50 €)')) {
      return;
    }
    setBackfillBusy(true);
    try {
      const r = await api.post<{ enqueued: number; estimated_cost_eur_max: number }>(
        '/admin/ocr/backfill-mistral',
        { limit: 50 },
      );
      toast.success(`${r.enqueued} re-OCR enqueueés (coût max ~${r.estimated_cost_eur_max} €)`);
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Erreur backfill');
    } finally {
      setBackfillBusy(false);
    }
  }, [load]);

  if (loading) return <p className="text-sm text-text-secondary">Chargement…</p>;
  if (error) return <p className="text-sm text-cat-def">Erreur : {error}</p>;
  if (!data) return <p className="text-sm text-text-secondary">Aucune donnée.</p>;

  // Pivot counts → matrice par bucket × status pour les KPI cards.
  const matrix: Record<string, Record<string, number>> = {};
  for (const c of data.counts) {
    const bucket = (matrix[c.source_type] ??= {});
    bucket[c.ocr_status] = c.n;
  }

  return (
    <div className="space-y-6">
      {/* KPI counts par bucket × status */}
      <section>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {BUCKETS.map((b) => {
            const row = matrix[b.key] || {};
            const done = row.done ?? row.processed ?? 0;
            const pending = row.pending ?? 0;
            const inProgress = row.in_progress ?? 0;
            const failed = row.failed ?? 0;
            return (
              <div key={b.key} className="rounded-md border border-rule bg-paper-card p-4">
                <div className="mb-2 text-[11px] uppercase tracking-wider text-text-tertiary">
                  {b.label}
                </div>
                <div className="grid grid-cols-4 gap-1.5 text-center text-[12px]">
                  <StatBox label="OK" n={done} color="text-cat-fav" />
                  <StatBox label="pend" n={pending} color="text-text-secondary" />
                  <StatBox label="cours" n={inProgress} color="text-cat-imp-text" />
                  <StatBox label="fail" n={failed} color="text-cat-def" />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <Btn variant="ghost" onClick={() => void load()}>
            Rafraîchir
          </Btn>
          <Btn variant="primary" onClick={() => void backfillLowConf()} disabled={backfillBusy}>
            {backfillBusy ? '…' : 'Backfill Mistral basse confiance (50)'}
          </Btn>
        </div>
      </section>

      {/* Listes détaillées par bucket */}
      {BUCKETS.map((b) => {
        const rows = data[b.detail];
        if (!rows.length) return null;
        return (
          <section key={b.key}>
            <h2 className="mb-2 text-sm font-semibold text-text-primary">
              {b.label} ·{' '}
              <span className="text-text-tertiary">{rows.length} ligne(s) à surveiller</span>
            </h2>
            <div className="overflow-hidden rounded-md border border-rule">
              <table className="w-full text-[11.5px]">
                <thead className="bg-paper-subtle text-left text-[10.5px] uppercase tracking-wider text-text-tertiary">
                  <tr>
                    <th className="px-2 py-1.5">Status</th>
                    <th className="px-2 py-1.5">Engine</th>
                    <th className="px-2 py-1.5">Dossier</th>
                    <th className="px-2 py-1.5">Fichier</th>
                    <th className="px-2 py-1.5">Upload</th>
                    <th className="px-2 py-1.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-rule odd:bg-paper-card">
                      <td
                        className={`px-2 py-1.5 font-medium ${
                          STATUS_COLOR[r.ocr_status] ?? 'text-text-primary'
                        }`}
                      >
                        {r.ocr_status}
                      </td>
                      <td className="px-2 py-1.5 text-text-tertiary">{r.ocr_engine ?? '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-[10.5px]">
                        {r.dossier_ref ?? '—'}
                      </td>
                      <td
                        className="max-w-sm truncate px-2 py-1.5"
                        title={r.filename_original}
                      >
                        {r.filename_original}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-text-tertiary">
                        {fmtDateTime(r.uploaded_at)}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Btn
                          variant="ghost"
                          onClick={() => void reocrMistral(b.key, r.id)}
                          disabled={actingId === r.id}
                        >
                          {actingId === r.id ? '…' : 'Re-OCR Mistral'}
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function StatBox({ label, n, color }: { label: string; n: number; color: string }) {
  return (
    <div className="rounded-sm bg-paper-subtle py-1.5">
      <div className={`text-base font-semibold ${color}`}>{n}</div>
      <div className="text-[9.5px] uppercase tracking-wider text-text-tertiary">{label}</div>
    </div>
  );
}
